<template>
	<div
		class="page"
		:class="pageClass"
	>
		<div class="page__header">
			<Header />
		</div>

		<div class="page__main-content">
			<router-view></router-view>
		</div>

		<div class="page__footer">
			<Footer />
		</div>

		<CookiesTooltip />
		<PopupPolicy />
	</div>
</template>

<script>
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import CookiesTooltip from '@/components/CookiesTooltip.vue'
import PopupPolicy from '@/components/PopupPolicy'

export default {
	components: {
		Header,
		Footer,
		CookiesTooltip,
		PopupPolicy,
	},

	computed: {
		pageClass() {
			return {
				'theme-light': this.$store.state.general.theme === 'light',
				'theme-dark': this.$store.state.general.theme === 'dark',
				home: this.$route.name === 'Home',
			}
		},
	},
}
</script>

<style lang="scss">
@import '@/sass/_variables';
@import '@/sass/nprogress';
@import '@/sass/globalcode-icons';
@import 'bootstrap/scss/bootstrap-reboot.scss';
@import 'bootstrap/scss/bootstrap-grid.scss';
@import 'bootstrap/scss/utilities/_spacing.scss';

// GENERAL ==================================================================
.grecaptcha-badge {
	visibility: hidden;
}

body {
	font-family: 'Montserrat', sans-serif;
	font-size: 14px;
	line-height: 1.32;
	word-wrap: break-word;
	min-width: 320px;
}
@media (max-width: $md) {
	body {
		font-family: 'SF UI Text', sans-serif;
	}
}

* {
	outline: none !important;
}

.page {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 100vh;
	width: 100%;
	overflow-x: hidden;
	position: relative;
	z-index: 1;

	// Тёмная тема по умолчанию
	&,
	&.theme-dark {
		background: #020e14
			linear-gradient(
				90deg,
				rgba(102, 0, 204, 0.1) 5.34%,
				rgba(1, 51, 255, 0.1) 91.03%
			);
		color: #fff;
	}

	&.theme-light {
		// Светлая тема. Но не для мобилок (дизайнерское решение)
		@media (min-width: ($md+1)) {
			background: #fff;
			color: #020e14;
		}
	}
}
.page__header {
	flex: 0 0 auto;
}
.page__main-content {
	flex: 1 0 auto;
}
.page__footer {
	flex: 0 0 auto;
}

.text-center {
	text-align: center;
}
.text-right {
	text-align: right;
}

.w-100 {
	width: 100% !important;
}

// BUTTONS ====================================================================
.btn-group {
	display: flex;
	&__btn {
		display: flex;
		align-items: center;
		justify-content: center;
		border: none;
		text-decoration: none !important;
		color: #ffffff;
		font-size: 11px;
		font-weight: 500;
		padding: 0 8px;
		height: 24px;
		line-height: 24px;
		background-color: #2f23a3;
		transition: 0.2s;
		&:first-child {
			border-radius: 100px 0 0 100px;
			padding-left: 12px;
		}
		&:last-child {
			border-radius: 0 100px 100px 0;
			padding-right: 12px;
		}
		&:hover {
			background-color: lighten(#0d0e4c, 8%);
		}
		&.active {
			background-color: #0d0e4c;
		}
	}
}
.theme-light .btn-group {
	&__btn {
		border: 1px solid #f2f2f2;
		background-color: #fff;
		color: #020e14;
		box-shadow: 0px 15px 60px rgba(13, 30, 81, 0.33);
		&:hover {
			background-color: #f2f2f2;
			border-color: darken(#f2f2f2, 15%);
		}
		&.active {
			background-color: #0d0d0d;
			border-color: #0d0d0d;
			color: #ffffff;
			box-shadow: none;
		}
	}
}

.btn-circle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: relative;
	width: 48px;
	height: 48px;
	border: none;
	text-decoration: none !important;
	position: relative;
	background: $primary;
	transition: 0.3s;
	border-radius: 100%;
	line-height: 48px;
	color: #ffffff;
	font-size: 24px;
	&:hover {
		color: #ffffff;
		background: darken($primary, 8%);
	}
}

.btn-primary {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: relative;
	font-weight: 600;
	font-size: 18px;
	line-height: 30px;
	color: #fff !important;
	border-radius: 14px;
	text-align: center;
	border: none;
	text-decoration: none !important;
	z-index: 1;
	padding: 13px 24px;
	height: 56px;
	width: 318px;
	max-width: 100%;
	background: #0166ff;
	transition: 0.3s;
	box-sizing: border-box;
	&::before {
		content: '';
		position: absolute;
		height: 12px;
		width: calc(100% - 44px);
		left: 22px;
		bottom: 0;
		background: linear-gradient(90deg, #6600cc 5.34%, #0133ff 91.03%);
		opacity: 0.66;
		box-shadow: 0px 9px 30px rgba(14, 84, 221, 1);
		filter: blur(30px);
		border-radius: 100px;
		transition: 0.8s ease-in-out;
		z-index: -1;
	}
	.gradient {
		position: absolute;
		width: 100%;
		height: 100%;
		overflow: hidden;
		border-radius: 14px;
		pointer-events: none;
		&::after {
			content: '';
			position: absolute;
			z-index: -1;
			top: 0;
			left: -100%;
			width: 200%;
			height: 100%;
			background: linear-gradient(
				90deg,
				#0133ff 0%,
				#6600cc 40%,
				#6600cc 55%,
				#0133ff 95%
			);
			transition: 0.8s ease-in-out;
		}
	}
	&:hover {
		background: lighten($accent, 10%);
		&::before {
			opacity: 1;
		}
		.gradient::after {
			left: 0;
		}
	}
	&.bold {
		font-weight: 700;
		text-transform: uppercase;
	}
	&.small {
		font-size: 14px;
		padding: 9px 24px;
		height: 48px;
		width: 208px;
	}
	&.loading {
		animation: btnPrimaryLoading infinite 3s;
		pointer-events: none;
		.gradient {
			opacity: 0;
		}
	}
	&.reverse-color {
		background: #fff;
		color: $primary !important;
		box-shadow: 0px 5px 20px rgba(0, 0, 0, 0.07);
		&::before {
			display: none;
		}
		&:hover {
			box-shadow: 0 5px 15px rgba(#000, 0.2);
		}
	}
}
@media (max-width: $md) {
	.btn-primary {
		height: 56px;
	}
}
@keyframes btnPrimaryLoading {
	0%,
	100% {
		background-color: lighten($accent, 20%);
	}
	50% {
		background-color: $accent;
	}
}

.btn-outlined {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	position: relative;
	text-decoration: none !important;
	border: 1px solid $primary;
	border-radius: 14px;
	background-color: transparent;
	padding: 12px 22px;
	height: 56px;
	color: $primary;
	font-size: 18px;
	width: 280px;
	max-width: 100%;
	cursor: pointer;
	font-weight: 600;
	transition: 0.3s;
	user-select: none;
	box-sizing: border-box;
	&.has-icon {
		padding-right: 48px;
		i {
			position: absolute;
			font-size: 24px;
			right: 16px;
			top: 0;
			line-height: 56px;
			&.icon-chevron-right {
				font-size: 18px;
			}
		}
		&.small i {
			line-height: 48px;
		}
	}
	&:hover {
		color: #ffffff;
		background-color: $primary;
	}
	&.dark {
		border-color: #fff;
		color: #ffffff;
		&:hover {
			color: $primary;
			background-color: #fff;
		}
	}
	&.text-white {
		color: #fff;
	}
	&.small {
		height: 48px;
	}
}
@media (max-width: $md) {
	.btn-primary,
	.btn-outlined {
		font-size: 17px;
	}
}

.btn-close {
	position: absolute;
	right: 16px;
	top: 16px;
	border: none;
	padding: 0;
	background: transparent;
	font-size: 24px;
	width: 28px;
	height: 28px;
	line-height: 1;
	border-radius: 100%;
	cursor: pointer;
	transition: 0.3s;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	&::before {
		content: '\e901';
		font-family: 'globalcode-icons' !important;
	}
	&:hover {
		background-color: rgba(#fff, 0.2);
	}
	&.dark {
		color: #020e14;
		&:hover {
			background-color: rgba(#020e14, 0.1);
		}
	}
}

.btn-back {
	display: inline-flex;
	align-items: center;
	border: none;
	background: transparent;
	color: #0a84ff;
	font-size: 17px;
	padding: 0 6px 0 0;
	letter-spacing: -0.4px;
	transition: 0.3s;
	&::before {
		content: '\e912';
		font-family: 'globalcode-icons' !important;
		font-size: 18px;
		margin-right: 4px;
	}
	&:hover {
		color: #ffffff;
	}
}

.swiper-prev-1,
.swiper-next-1 {
	position: absolute;
	z-index: 3;
	width: 64px;
	height: 64px;
	top: calc(50% - 32px);
	background-color: rgba(#020e14, 0.75);
	color: #ffffff;
	border-radius: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	cursor: pointer;
	transition: 0.3s;
	&::before {
		font-family: 'globalcode-icons' !important;
	}
	&:hover {
		background-color: rgba($primary, 0.75);
		box-shadow: 0 5px 10px rgba(#000, 0.15);
	}
	&.swiper-button-disabled {
		pointer-events: none;
		opacity: 0;
	}
}
.swiper-prev-1 {
	left: 48px;
	&::before {
		content: '\e912';
	}
	&.swiper-button-disabled {
		left: -48px;
	}
}
.swiper-next-1 {
	right: 48px;
	&::before {
		content: '\e913';
	}
	&.swiper-button-disabled {
		right: -48px;
	}
}

.swiper-prev-2,
.swiper-next-2 {
	position: absolute;
	z-index: 3;
	width: 24px;
	height: 24px;
	top: calc(50% - 12px);
	color: #ffffff;
	border-radius: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 24px;
	cursor: pointer;
	transition: 0.3s;
	&::before {
		font-family: 'globalcode-icons' !important;
	}
	&:hover {
		color: lighten($primary, 35%);
	}
	&.swiper-button-disabled {
		pointer-events: none;
		opacity: 0;
	}
}
.swiper-prev-2 {
	left: 0;
	&::before {
		content: '\e912';
	}
	&.swiper-button-disabled {
		left: -24px;
	}
}
.swiper-next-2 {
	right: 0;
	&::before {
		content: '\e913';
	}
	&.swiper-button-disabled {
		right: -24px;
	}
}
@media (max-width: ($xl + 42px)) {
	.swiper-prev-2 {
		left: 16px;
	}
	.swiper-next-2 {
		right: 16px;
	}
}

.swiper-pagination-1 {
	text-align: center;
	display: flex;
	justify-content: center;
	.swiper-pagination-bullet {
		margin: 4px;
		background-color: #fff;
		opacity: 0.3;
		transition: 0.3s;
		cursor: pointer;
		&:hover {
			opacity: 0.6;
		}
		&-active {
			opacity: 1 !important;
		}
	}
}

.swiper-pagination-2 {
	display: flex;
	border-radius: 3px;
	height: 2px;
	overflow: hidden;
	.swiper-pagination-bullet {
		flex-grow: 1;
		border-radius: 0;
		background: #30393e;
		height: 2px;
		padding: 0;
		margin: 0 !important;
		transition: 0.3s;
		cursor: pointer;
		opacity: 1;
		&:hover,
		&-active {
			background: #0a84ff;
		}
	}
}

// FORM INPUTS =================================================================
label {
	margin: 0;
}
.form {
	&__title {
		font-weight: 800;
		font-size: 21px;
		line-height: 32px;
		color: #3b3b3b;
		margin-bottom: 24px;
	}
	&__label {
		font-size: 18px;
		line-height: 48px;
		margin-bottom: 16px;
	}
}

.checkbox__label,
.checkbox__custom-input,
input[type='tel'],
input[type='text'],
textarea {
	margin-bottom: 24px;

	&.error {
		border-color: red;
	}
}

input[type='tel'],
input[type='text'],
textarea {
	display: block;
	width: 100%;
	border-radius: 3px;
	background: #fff;
	border: 1px solid #e0e0e0;
	transition: 0.3s;
	box-shadow: none;
	color: #000000;

	&:focus {
		border-color: darken(#e0e0e0, 20%);
		box-shadow: 0 0 15px rgba(#000, 0.1);
	}
}

input[type='tel'],
input[type='text'] {
	height: 56px;
	padding: 0 16px;
}

textarea {
	resize: none;
	padding: 16px;
	height: 110px;
}

::placeholder {
	color: #bdbdbd;
}

.required-input {
	position: relative;
	&::before {
		content: '*';
		position: absolute;
		font-size: 16px;
		color: #3b3b3b;
		top: 3px;
		left: 5px;
		pointer-events: none;
	}
}

.input-errors {
	font-size: 12px;
	margin-top: -18px;
	margin-bottom: 12px;
	color: red;
	.error-chip {
		display: inline-block;
		padding: 2px 8px;
		border-radius: 14px;
		border: 1px solid rgba(red, 0.8);
		background-color: rgba(#fff, 0.8);
		margin-top: 4px;
	}
}

.inputfile {
	margin-bottom: 24px;
	input {
		width: 0.1px;
		height: 0.1px;
		opacity: 0;
		overflow: hidden;
		position: absolute;
		z-index: -1;
	}
	&__title {
	}
	&__result {
		font-size: 13px;
		text-align: right;
		color: #333;
		i {
			color: $primary;
		}
	}
	.input-errors {
		text-align: right;
		margin-top: -5px;
	}
}

@media (max-width: $md) {
	.form {
		&__title {
			font-size: 24px;
			line-height: 32px;
			margin-bottom: 24px;
		}
		&__label {
			font-size: 11px;
			line-height: 18px;
			margin-bottom: 12px;
			i {
				color: $primary;
				font-size: 24px;
			}
		}
	}
	input[type='tel'],
	input[type='text'],
	textarea {
		border-radius: 10px;
		margin-bottom: 16px;
		letter-spacing: -0.4px;
		font-size: 17px;
		&.error {
			border-color: red;
		}
		&:not(textarea) {
			height: 36px;
			padding: 0 12px;
		}
		&:focus {
			border-color: rgba(#fff, 0.7);
			box-shadow: none;
		}
	}
	textarea {
		padding: 6px 12px;
		height: 80px;
		font-size: 15px;
	}
	::placeholder {
		color: #e0e0e0;
	}
	.required-input::before {
		display: none;
	}
	.inputfile {
		margin-bottom: 16px;
		&__title {
			color: #ffffff;
			font-size: 11px;
			margin-bottom: 10px;
		}
		&__result,
		.input-errors {
			text-align: left;
		}
		.input-errors {
			margin-top: -4px;
		}
	}
	.input-errors {
		font-size: 11px;
		margin-top: -12px;
		font-weight: normal;
		letter-spacing: 0.2px;
		.error-chip {
			display: inline-block;
			padding: 2px 8px;
			border-radius: 14px;
			border: 1px solid rgba(red, 0.8);
			background-color: rgba(#fff, 0.8);
			margin-top: 4px;
		}
	}
}

// HEADERS ====================================================================
h1,
.h1,
.home-page h2,
.home-page .h2 {
	font-size: 56px;
	margin: 80px 0 56px;
	line-height: 1.13;
	font-weight: 800;
}
@media (max-width: $lg) {
	h1,
	.h1,
	.home-page h2,
	.home-page h2 {
		font-size: 40px;
		margin: 60px 0 40px;
	}
}
@media (max-width: $md) {
	h1,
	.h1,
	.home-page h2,
	.home-page h2 {
		font-size: 24px;
		line-height: 1.33;
		margin: 30px 0 20px;
	}
}

// CONTENT ELEMENTS ============================================================
.content {
	font-size: 15px;
	line-height: 1.5;
	p {
		margin-bottom: 24px;
	}
	ul {
		list-style-type: none;
		padding: 0;
		margin-bottom: 24px;
		font-size: 18px;
		li {
			position: relative;
			padding-left: 60px;
			&:not(:last-child) {
				margin-bottom: 16px;
			}
			&::before {
				content: '—';
				position: absolute;
				left: 0;
				top: -8px;
				font-size: 36px;
				line-height: 1;
				color: #2f80ed;
			}
		}
	}
}
@media (max-width: $lg) {
	.content {
		ul {
			font-size: 16px;
			li::before {
				top: -10px;
			}
		}
	}
}
@media (max-width: $md) {
	.content {
		font-size: 13px;
		ul {
			margin-bottom: 0;
			font-size: 15px;
			li {
				position: relative;
				padding-left: 32px;
				font-weight: 500;
				&:not(:last-child) {
					margin-bottom: 16px;
				}
				&::before {
					content: '\e900';
					font-family: 'globalcode-icons' !important;
					font-size: 26px;
					top: -3px;
					color: $primary;
				}
			}
		}
	}
}

.link,
.link-light,
.content a {
	color: $primary;
	text-decoration: none !important;
	transition: 0.3s;
	border-bottom: 1px dotted transparent;
	&:hover {
		color: #fff;
		border-color: rgba(#fff, 0.3);
	}
}
.theme-light .link,
.theme-light .content a,
.link-light {
	&:hover {
		color: $accent;
		border-color: rgba($accent, 0.3);
	}
}

ol {
	padding: 0;
	list-style: none;
	counter-reset: li;
	li {
		padding: 0 !important;
		&:not(:last-child) {
			margin-bottom: 12px !important;
		}
		&::before {
			content: counters(li, '.') '. ' !important;
			counter-increment: li !important;
			font-size: inherit !important;
			line-height: inherit !important;
			color: inherit !important;
			position: static !important;
		}
	}
}

// OTHER =======================================================================
.actions-list {
	&__btn {
		display: flex;
		align-items: center;
		position: relative;
		padding: 10px 4px;
		font-size: 15px;
		font-weight: 500;
		color: #ffffff;
		border-bottom: 1px solid #30393e;
		cursor: pointer;
		text-decoration: none !important;
		transition: 0.3s;
		span {
			flex: 1;
		}
		&::after {
			content: '\e913';
			font-family: 'globalcode-icons' !important;
			display: block;
			font-size: 20px;
			color: #2f80ed;
			flex-shrink: 1;
			margin: 0 0 0 16px;
		}
		i[class^='icon-'] {
			margin-right: 10px;
			width: 24px;
			height: 24px;
			display: flex;
			align-items: center;
			justify-content: center;
			color: $primary;
			font-size: 24px;
		}
		&:hover {
			color: #ffffff;
			background-color: rgba(#fff, 0.04);
		}
	}
}

.checkbox-list {
	.checkbox {
		display: flex;
		font-size: 17px;
		line-height: 20px;
		padding: 10px 4px;
		border-bottom: 1px solid #30393e;
		cursor: pointer;
		&:hover {
			background-color: rgba(#fff, 0.04);
		}
	}
}

.chips-container {
	margin: -7px -4px;
}
.chip {
	display: inline-flex;
	border: 1px solid #e5e5e5;
	box-sizing: border-box;
	background-color: transparent;
	box-shadow: 0px 4px 10px rgba(#000, 0.1);
	color: #ffffff;
	border-radius: 24px;
	padding: 8px 24px;
	margin: 7px 4px;
	font-size: 18px;
	font-weight: 500;
	line-height: 20px;
	cursor: pointer;
	user-select: none;
	transition: 0.3s;
	&:hover {
		background-color: rgba(#fff, 0.1);
	}
	&.active {
		color: #2f80ed;
	}
	&.dark {
		box-shadow: none !important;
		color: #333;
		&:hover {
			background-color: rgba(#333, 0.05);
		}
		&.active {
			background-color: $primary;
			border-color: $primary;
			color: #ffffff;
			&:hover {
				background-color: rgba($primary, 0.8);
			}
		}
	}
}
.theme-light .chip {
	color: #2f80ed;
	box-shadow: none;
	&:hover {
		background-color: rgba($primary, 0.07);
	}
	&.active {
		background-color: $primary;
		border-color: $primary;
		color: #ffffff;
		&:hover {
			background-color: rgba($primary, 0.8);
		}
	}
}
@media (max-width: $lg) {
	.chips-container {
		margin: -6px -5px;
	}
	.chip {
		padding: 6px 18px;
		margin: 6px 5px;
		box-shadow: 0px 3px 8px rgba(#000, 0.08);
	}
}
</style>
